/**
 * AI Suggest Utilities — Rule-Based Field Suggestions
 *
 * Provides a free, client-side suggestion engine that recommends values for
 * empty asset form fields based on:
 *   1. The geographically nearest same-type asset ("copy from nearest")
 *   2. The most frequently used value across all same-type assets ("most common")
 *
 * No API calls needed — runs entirely in the browser using serviceData.
 */

import { ASSET_ID_FIELD_MAP } from "./assetIdUtils";

/**
 * Fields that should never be suggested (system / auto-generated).
 */
const SKIP_FIELDS = new Set([
    "GlobalID",
    "Created_User",
    "Created_Date",
    "Last_Editor",
    "Last_Edited_Date",
    "ClientID",
    "DeleteFacility",
    "Latitude",
    "Longitude",
    "LineJSON",
    "HydrantID",
    "ManholeID",
    "ValveID",
    "TrafficSignID",
    "WaterMeterID",
    "WaterLineID",
    "SewerLineID",
    "DaysSinceLastInspection",
    "assetFiles",
]);

/**
 * Field types that should not be suggested (dates are context-dependent).
 */
const SKIP_TYPES = new Set(["date"]);

/**
 * Haversine distance in meters between two lat/lng points.
 */
function haversineDistance(lat1, lng1, lat2, lng2) {
    const R = 6371000; // Earth radius in meters
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) *
            Math.cos(toRad(lat2)) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Find the nearest same-type asset that has a non-empty value for `fieldName`.
 *
 * @param {Array} sameTypeAssets - assets of the same type
 * @param {number} lat - current asset latitude
 * @param {number} lng - current asset longitude
 * @param {string} fieldName - the field to look for
 * @returns {{ value: any, distance: number } | null}
 */
function findNearestWithField(sameTypeAssets, lat, lng, fieldName) {
    let bestValue = null;
    let bestDistance = Infinity;

    for (const asset of sameTypeAssets) {
        const aLat = parseFloat(asset.Latitude);
        const aLng = parseFloat(asset.Longitude);
        if (isNaN(aLat) || isNaN(aLng)) continue;

        const val = asset[fieldName];
        if (val === undefined || val === null || String(val).trim() === "") continue;

        const dist = haversineDistance(lat, lng, aLat, aLng);
        if (dist < bestDistance) {
            bestDistance = dist;
            bestValue = val;
        }
    }

    if (bestValue !== null) {
        return { value: bestValue, distance: bestDistance };
    }
    return null;
}

/**
 * Get the most common non-empty value for a field across all same-type assets.
 *
 * @param {Array} sameTypeAssets - assets of the same type
 * @param {string} fieldName - the field to analyze
 * @returns {string|null} the most frequent value, or null
 */
function getMostCommonValue(sameTypeAssets, fieldName) {
    const counts = {};
    for (const asset of sameTypeAssets) {
        const val = asset[fieldName];
        if (val === undefined || val === null || String(val).trim() === "") continue;
        const key = String(val);
        counts[key] = (counts[key] || 0) + 1;
    }

    let best = null;
    let bestCount = 0;
    for (const [val, count] of Object.entries(counts)) {
        if (count > bestCount) {
            bestCount = count;
            best = val;
        }
    }
    return best;
}

/**
 * Find the top N nearest same-type assets (for Gemini context).
 *
 * @param {Array} sameTypeAssets - assets of the same type
 * @param {number} lat - current asset latitude
 * @param {number} lng - current asset longitude
 * @param {number} n - number of nearest assets to return
 * @returns {Array} top N nearest assets sorted by distance
 */
export function findNearestAssets(sameTypeAssets, lat, lng, n = 3) {
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) return [];

    const withDist = sameTypeAssets
        .filter((a) => {
            const aLat = parseFloat(a.Latitude);
            const aLng = parseFloat(a.Longitude);
            return !isNaN(aLat) && !isNaN(aLng);
        })
        .map((a) => ({
            asset: a,
            distance: haversineDistance(lat, lng, parseFloat(a.Latitude), parseFloat(a.Longitude)),
        }))
        .sort((a, b) => a.distance - b.distance);

    return withDist.slice(0, n).map((item) => item.asset);
}

/**
 * Suggest values for empty fields on the current asset form.
 *
 * Strategy per empty field:
 *   1. If lat/lng available: copy value from the nearest same-type asset
 *   2. Fallback: use the most common value across all same-type assets
 *
 * @param {string} assetType - e.g., "Hydrant", "Valve"
 * @param {Object} assetRecord - current form values
 * @param {Array} serviceData - all loaded assets (point + line)
 * @param {Array} inputObjectArray - field definitions for this asset type
 * @returns {Object} { fieldName: { value, source } }
 *   source is "nearest" or "common"
 */
export function suggestFieldValues(assetType, assetRecord, serviceData, inputObjectArray) {
    const suggestions = {};

    if (!inputObjectArray || !serviceData || serviceData.length === 0) {
        return suggestions;
    }

    // Identify the Client ID field for this asset type (skip it in suggestions)
    const clientIdField = ASSET_ID_FIELD_MAP[assetType];

    // Filter to same-type assets using the Client ID field as a type marker
    const sameTypeAssets = clientIdField
        ? serviceData.filter(
              (a) => a[clientIdField] !== undefined && a[clientIdField] !== null
          )
        : [];

    if (sameTypeAssets.length === 0) {
        return suggestions;
    }

    // Current asset location
    const lat = parseFloat(assetRecord?.Latitude);
    const lng = parseFloat(assetRecord?.Longitude);
    const hasLocation = !isNaN(lat) && !isNaN(lng);

    for (const field of inputObjectArray) {
        // Skip non-display, system, date, and already-filled fields
        if (!field.display) continue;
        if (SKIP_FIELDS.has(field.name)) continue;
        if (SKIP_TYPES.has(field.type)) continue;
        if (field.name === clientIdField) continue;

        // Only suggest for empty fields
        const currentVal = assetRecord?.[field.name];
        if (currentVal !== undefined && currentVal !== null && String(currentVal).trim() !== "") {
            continue;
        }

        // Strategy 1: Copy from nearest same-type asset
        if (hasLocation) {
            const nearest = findNearestWithField(sameTypeAssets, lat, lng, field.name);
            if (nearest) {
                suggestions[field.name] = {
                    value: nearest.value,
                    source: "nearest",
                };
                continue;
            }
        }

        // Strategy 2: Most common value
        const common = getMostCommonValue(sameTypeAssets, field.name);
        if (common) {
            suggestions[field.name] = {
                value: common,
                source: "common",
            };
        }
    }

    return suggestions;
}
